import { i18n } from "@lingui/core"

import { getValidateEmailUrl, VALID_EMAIL } from "../constants"
import { debouncePromise } from "../debounce-promise"
import { getHubspotBlockedEmailLocalizedMessage } from "../requests/hubspot"
import { EMAIL_REGEX } from "../validation"

// Allow any Unicode letter (Lu, Ll, Lt, Lm, Lo), any combining mark (Mn, Mc),
// space, hyphen-minus, en-dash, various apostrophes, middle dot, period,
// comma, parentheses, click consonant symbols, equals sign, Hebrew maqaf.
// Block URL-like strings
const SAFE_NAME_REGEX =
  /^[\p{L}\p{M} \-\u2013'\u2018\u2019\u02BB\u00B7.,()!ǀǁǂ=\u05BE]+$/u

/**
 * Validates that the given name contains only allowed characters.
 *
 * @param name – the name string to validate
 * @returns true if valid, false otherwise
 */
export function isValidName(name: string) {
  return SAFE_NAME_REGEX.test(name)
}

export const requiredField = (msg: () => string) => (value: string) =>
  !value ? msg() : ""

export const validateOtherField = requiredField(() =>
  i18n._("Please enter the other value"),
)
export const validatePayoutToField = requiredField(() =>
  i18n._("Select who you are sending to"),
)
export const validateLeadField = requiredField(() =>
  i18n._("Select how you heard about us"),
)

export const validateFirstName = (value: string) => {
  if (!value) {
    return i18n._("A first name is required")
  }
  if (!isValidName(value)) {
    return i18n._("First name contains invalid characters")
  }
  return ""
}

export const validateLastName = (value: string) => {
  if (!value) {
    return i18n._("A last name is required")
  }
  if (!isValidName(value)) {
    return i18n._("Last name contains invalid characters")
  }
  return ""
}

export const validateJobTitle = (value: string) => {
  if (!value) {
    return i18n._("A job title is required")
  }
  if (!isValidName(value)) {
    return i18n._("Job title contains invalid characters")
  }
  return ""
}

export const validateSpend = requiredField(() =>
  i18n._("Select your annual spend amount"),
)

const getValidateEmailText = (email: string) =>
  fetch(getValidateEmailUrl(email))
    .then(res => {
      if (res.status !== 200) {
        throw new Error(res.statusText)
      }
      return res.text()
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error("validateEmailWithApi error", error)
      // In case of error we want to return true to avoid blocking the user
      return VALID_EMAIL
    })

const emailValidationResultsCache = new Map<string, Promise<string>>()
export const validateEmailWithApi = async (email: string) => {
  const cachedResult = emailValidationResultsCache.get(email)
  if (cachedResult) {
    return cachedResult
  }
  const response = getValidateEmailText(email)
  emailValidationResultsCache.set(email, response)
  return response
}

// Ability to switch on/off neverbounce via vercel
const debouncedValidateEmailWithApi =
  process.env.GATSBY_ENABLE_NEVERBOUNCE === "true"
    ? debouncePromise(validateEmailWithApi, 800)
    : () => VALID_EMAIL

export function validateName(name: string) {
  if (!isValidName(name)) {
    return i18n._("Invalid name")
  }
  return ""
}

export const isDomainInBlacklist = (blacklist: string[], email: string) => {
  const userEmailDomain = email.split("@")?.[1]?.toLowerCase()
  const isBlocked = blacklist.some(domain => domain === userEmailDomain)
  return isBlocked
}

export const validateEmail =
  (invalidEmails: string[] = [], blacklist: string[] = []) =>
  async (value: string) => {
    if (invalidEmails.includes(value) || !value || !EMAIL_REGEX.test(value)) {
      return getHubspotBlockedEmailLocalizedMessage()
    }

    // we do not want this inside the debouncedValidateEmailWithApi to avoid debounce wait
    if (emailValidationResultsCache.has(value)) {
      const cacheResult = await emailValidationResultsCache.get(value)
      return cacheResult === VALID_EMAIL
        ? ""
        : getHubspotBlockedEmailLocalizedMessage()
    }
    // moved separately so we do not have to iterate over blacklist every time we validate.
    // it has ~6000 items
    if (isDomainInBlacklist(blacklist, value)) {
      return getHubspotBlockedEmailLocalizedMessage()
    }
    return (await debouncedValidateEmailWithApi(value)) === VALID_EMAIL
      ? ""
      : getHubspotBlockedEmailLocalizedMessage()
  }
